import type { PixelData } from '@vikings/types';

export interface AnimationConfig {
  frames: PixelData[];
  fps: number;
  loop: boolean;
  pingPong: boolean;
}

/**
 * Animation controller — manages frame switching for pixel-art animations.
 * Supports loop and ping-pong modes.
 */
export class Animator {
  private frames: PixelData[];
  private fps: number;
  private loop: boolean;
  private pingPong: boolean;

  private currentFrame: number = 0;
  private elapsed: number = 0;
  private direction: 1 | -1 = 1;
  private running: boolean = true;

  constructor(config: AnimationConfig) {
    this.frames = config.frames;
    this.fps = config.fps;
    this.loop = config.loop;
    this.pingPong = config.pingPong;
  }

  /**
   * Update animation state. Call every frame with delta time in seconds.
   */
  update(deltaTime: number): void {
    if (!this.running || this.frames.length <= 1) return;

    this.elapsed += deltaTime;
    const frameDuration = 1 / this.fps;

    while (this.elapsed >= frameDuration) {
      this.elapsed -= frameDuration;
      this.advanceFrame();
    }
  }

  private advanceFrame(): void {
    if (this.pingPong) {
      this.currentFrame += this.direction;

      if (this.currentFrame >= this.frames.length - 1) {
        this.currentFrame = this.frames.length - 1;
        this.direction = -1;
      } else if (this.currentFrame <= 0) {
        this.currentFrame = 0;
        this.direction = 1;
        if (!this.loop) {
          this.running = false;
        }
      }
    } else {
      this.currentFrame++;
      if (this.currentFrame >= this.frames.length) {
        if (this.loop) {
          this.currentFrame = 0;
        } else {
          this.currentFrame = this.frames.length - 1;
          this.running = false;
        }
      }
    }
  }

  /**
   * Get the current frame's pixel data.
   */
  getFrame(): PixelData {
    return this.frames[this.currentFrame]!;
  }

  /**
   * Get current frame index.
   */
  getFrameIndex(): number {
    return this.currentFrame;
  }

  /**
   * Reset animation to the beginning.
   */
  reset(): void {
    this.currentFrame = 0;
    this.elapsed = 0;
    this.direction = 1;
    this.running = true;
  }

  /**
   * Stop the animation.
   */
  stop(): void {
    this.running = false;
  }

  /**
   * Start/resume the animation.
   */
  start(): void {
    this.running = true;
  }

  /**
   * Check if animation is currently running.
   */
  isRunning(): boolean {
    return this.running;
  }
}
